/**
 * Lightweight cross-tab / cross-component event bus for workflow credentials.
 *
 * Whenever a credential is created, updated or deleted (manually or via an
 * OAuth popup) we want every other open NodeInspector that targets the same
 * workflow to refresh its credential preset list immediately - without the
 * user having to navigate away and back.
 *
 * The bus uses three transports so it works in every browser/tab combo:
 *
 *   1. window CustomEvent  - same window/tab
 *   2. localStorage event  - cross-tab (different windows on the same origin)
 *   3. BroadcastChannel    - same-origin (faster + ignored by Safari fallback)
 */

const EVENT_NAME = 'oda:workflow-credentials-changed';
const STORAGE_KEY = 'oda.workflow-credentials.changed';
const CHANNEL_NAME = 'oda.workflow-credentials';

type Detail = { workflowId?: string; ts?: number };

let channel: BroadcastChannel | null = null;
function getChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  if (!channel) {
    try {
      channel = new BroadcastChannel(CHANNEL_NAME);
    } catch {
      channel = null;
    }
  }
  return channel;
}

export function notifyCredentialsChanged(workflowId?: string): void {
  if (typeof window === 'undefined') return;
  const detail: Detail = { workflowId, ts: Date.now() };
  try {
    window.dispatchEvent(new CustomEvent<Detail>(EVENT_NAME, { detail }));
  } catch {
    // ignore
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(detail));
  } catch {
    // ignore (private mode / quota)
  }
  try {
    getChannel()?.postMessage(detail);
  } catch {
    // ignore
  }
}

export function subscribeCredentialsChanged(
  workflowId: string | undefined,
  callback: () => void
): () => void {
  if (typeof window === 'undefined') return () => {};

  const matches = (incoming?: string) =>
    !workflowId || !incoming || incoming === workflowId;

  const onCustom = (evt: Event) => {
    const detail = (evt as CustomEvent<Detail>).detail;
    if (matches(detail?.workflowId)) callback();
  };
  const onStorage = (evt: StorageEvent) => {
    if (evt.key !== STORAGE_KEY || !evt.newValue) return;
    try {
      const detail: Detail = JSON.parse(evt.newValue);
      if (matches(detail?.workflowId)) callback();
    } catch {
      callback();
    }
  };
  const onChannel = (evt: MessageEvent<Detail>) => {
    if (matches(evt.data?.workflowId)) callback();
  };

  window.addEventListener(EVENT_NAME, onCustom as EventListener);
  window.addEventListener('storage', onStorage);
  const ch = getChannel();
  if (ch) ch.addEventListener('message', onChannel as any);

  return () => {
    window.removeEventListener(EVENT_NAME, onCustom as EventListener);
    window.removeEventListener('storage', onStorage);
    if (ch) ch.removeEventListener('message', onChannel as any);
  };
}
